import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { watch, onMounted } from "vue";

// Interface for breadcrumb page
interface BreadcrumbPage {
  path: string;
  title: string;
  icon?: string;
  query?: Record<string, any>;
}

export function useBreadcrumb() {
  const store = useStore();
  const route = useRoute();

  // Function to get page title from route
  const getPageTitle = (
    routeName: string,
    params?: Record<string, any>
  ): string => {
    const titleMap: Record<string, string> = {
      users: "breadcrumb.users",
      user: "breadcrumb.user",
      organizations: "breadcrumb.organizations",
      organization: "breadcrumb.organization",
      invites: "breadcrumb.invites",
      invite: "breadcrumb.invite",
      entitlements: "breadcrumb.entitlements",
      entitlement: "breadcrumb.entitlement",
      templates: "breadcrumb.templates",
      template: "breadcrumb.template",
      products: "breadcrumb.products",
      product: "breadcrumb.product"
    };

    return titleMap[routeName] || routeName;
  };

  // Function to get page icon from route
  const getPageIcon = (routeName: string): string | undefined => {
    const iconMap: Record<string, string> = {
      users: "people",
      user: "person",
      organizations: "business",
      organization: "business",
      invites: "mail",
      invite: "mail",
      entitlements: "key",
      entitlement: "key",
      templates: "description",
      template: "description",
      products: "inventory",
      product: "inventory"
    };

    return iconMap[routeName];
  };

  // Function to add current page to breadcrumb history
  const addCurrentPageToBreadcrumb = () => {
    if (!route.name) {
      return;
    }

    const page: BreadcrumbPage = {
      path: route.path,
      title: getPageTitle(route.name as string, route.params),
      icon: getPageIcon(route.name as string),
      query: route.query
    };

    store.dispatch("breadcrumb/addPageToHistory", page);
  };

  // Function to clear breadcrumb history
  const clearBreadcrumbHistory = () => {
    store.dispatch("breadcrumb/clearHistory");
  };

  // Watch route changes and add to breadcrumb
  watch(
    () => [route.path, route.query],
    () => {
      addCurrentPageToBreadcrumb();
    },
    { immediate: true, deep: true }
  );

  // Add current page on mount
  onMounted(() => {
    addCurrentPageToBreadcrumb();
  });

  return {
    addCurrentPageToBreadcrumb,
    clearBreadcrumbHistory
  };
}
